import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { formatMoney, SAMPLE_BOX_DELIVERY_FEE } from '@hfb/shared';
import { useAuth } from '../context/AuthContext.jsx';
import { useHeroChrome } from '../context/HeroChrome.jsx';
import { useTheme } from '../context/ThemeContext.jsx';
import Button from './Button.jsx';
import Logo from './Logo.jsx';

const PUBLIC_LINKS = [
  { to: '/products', label: 'Shop' },
  { to: '/sample', label: 'Try a box' },
];

export default function Header() {
  const { user, isAdmin, logout } = useAuth();
  const { heroActive } = useHeroChrome();
  const { isDark, hasLightBand } = useTheme();
  // The hero band is dark in Light, Dark and Pop, but LIGHT in Mint and
  // Sunrise, so the header-over-hero and the logo follow the band's own
  // foreground rather than assuming a dark band.
  const heroDark = !hasLightBand;
  const logoLight = (heroActive && heroDark) || isDark;
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Any navigation closes the menu — otherwise it stays open over the new page.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // Over the hero the nav uses the band's own foreground token (light on the
  // dark Orchard band, dark on the light Mint band); elsewhere it's the bar.
  const navLinkClass = ({ isActive }) =>
    [
      'flex touch-target items-center rounded-full px-3.5 text-sm font-semibold transition-colors duration-150',
      heroActive
        ? isActive
          ? 'hero-fg bg-[rgb(var(--hero-line)/0.18)]'
          : 'hero-fg-soft sm:hover:bg-[rgb(var(--hero-line)/0.12)]'
        : isActive
          ? 'bg-leaf-100 text-heading'
          : 'text-ink-muted active:text-heading sm:hover:bg-oat/60 sm:hover:text-heading',
    ].join(' ');

  // Ghost buttons ("Sign in", the account name) take the hero foreground on the
  // band; `!` forces it over the variant's default leaf text.
  const ghostOnHero = heroActive
    ? '!text-[rgb(var(--hero-fg))] sm:hover:!bg-[rgb(var(--hero-line)/0.12)] active:!bg-[rgb(var(--hero-line)/0.12)]'
    : '';

  return (
    <header
      className={`pad-safe-top sticky top-0 z-40 transition-colors duration-300 ${
        heroActive
          ? 'border-b border-[rgb(var(--hero-line)/0.12)] bg-[rgb(var(--hero-chrome))] text-[rgb(var(--hero-fg))]'
          : 'border-b border-oat-dark/60 bg-surface/85 shadow-soft backdrop-blur-md'
      }`}
    >
      <div className="shell flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex touch-target items-center" aria-label="HealthyFruitsBox home">
          <Logo tone={logoLight ? 'light' : 'dark'} />
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
          {PUBLIC_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className={navLinkClass}>
              {link.label}
            </NavLink>
          ))}
          {user && (
            <NavLink to="/app" className={navLinkClass}>
              My box
            </NavLink>
          )}
          {user && (
            <NavLink to="/app/orders" className={navLinkClass}>
              My orders
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/admin" className={navLinkClass}>
              Admin
            </NavLink>
          )}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          {user ? (
            <>
              <Button variant="ghost" size="sm" to="/app/profile" className={ghostOnHero}>
                {user.fullName?.split(' ')[0] || 'Account'}
              </Button>
              <Button variant="secondary" size="sm" onClick={logout}>
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" to="/login" className={ghostOnHero}>
                Sign in
              </Button>
              <Button variant="accent" size="sm" to="/sample">
                Try a box: {formatMoney(SAMPLE_BOX_DELIVERY_FEE)}
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          className={`flex touch-target items-center justify-center rounded-full md:hidden ${
            heroActive ? 'hero-fg' : 'text-heading'
          }`}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {menuOpen ? (
              <path d="M6 6l12 12M18 6L6 18" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div id="mobile-menu" className="border-t border-oat-dark bg-surface md:hidden">
          <nav className="shell flex flex-col py-3" aria-label="Main">
            {PUBLIC_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className="flex touch-target items-center border-b border-oat-dark/50 text-base font-semibold text-heading"
              >
                {link.label}
              </NavLink>
            ))}
            {user && (
              <NavLink
                to="/app"
                className="flex touch-target items-center border-b border-oat-dark/50 text-base font-semibold text-heading"
              >
                My box
              </NavLink>
            )}
            {user && (
              <NavLink
                to="/app/orders"
                className="flex touch-target items-center border-b border-oat-dark/50 text-base font-semibold text-heading"
              >
                My orders
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/admin"
                className="flex touch-target items-center border-b border-oat-dark/50 text-base font-semibold text-heading"
              >
                Admin
              </NavLink>
            )}

            <div className="flex flex-col gap-2 pt-4">
              {user ? (
                <Button variant="secondary" onClick={logout} fullWidth>
                  Sign out
                </Button>
              ) : (
                <>
                  <Button variant="accent" to="/sample" fullWidth>
                    Try a box, pay only {formatMoney(SAMPLE_BOX_DELIVERY_FEE)} delivery
                  </Button>
                  <Button variant="secondary" to="/login" fullWidth>
                    Sign in
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
